import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { Add, Close, Delete, Edit, Refresh, ShoppingCart } from '@mui/icons-material'
import type { Pacote, PacoteInput } from '../../types'
import { apiService } from '../../services/api'

const brl = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const dialogBackdropSx = { bgcolor: 'rgba(0,0,0,0.78)' }
const dialogPaperSx = {
  bgcolor: 'rgba(15, 15, 28, 0.96)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 24px 90px rgba(0,0,0,0.65)'
}

const AdminPacotesPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<Pacote[]>([])
  const [refreshKey, setRefreshKey] = useState(0)

  const [createOpen, setCreateOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [vip, setVip] = useState(false)
  const [warzoneCoin, setWarzoneCoin] = useState(false)
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [preco, setPreco] = useState('29.90')
  const [pontos, setPontos] = useState('100')
  const [diasVip, setDiasVip] = useState('30')
  const [descricaoVip, setDescricaoVip] = useState('Acesso VIP')
  const [imagem, setImagem] = useState<File | null>(null)

  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Pacote | null>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editData, setEditData] = useState<Pacote | null>(null)
  const [editVip, setEditVip] = useState(false)
  const [editWarzoneCoin, setEditWarzoneCoin] = useState(false)
  const [editNome, setEditNome] = useState('')
  const [editDescricao, setEditDescricao] = useState('')
  const [editPreco, setEditPreco] = useState('0')
  const [editPontos, setEditPontos] = useState('0')
  const [editDiasVip, setEditDiasVip] = useState('0')
  const [editDescricaoVip, setEditDescricaoVip] = useState('')
  const [editImagem, setEditImagem] = useState<File | null>(null)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Pacote | null>(null)
  const [deleteContext, setDeleteContext] = useState<'list' | 'edit'>('list')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const sorted = useMemo(() => {
    const copy = [...items]
    copy.sort((a, b) => {
      return (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR')
    })
    return copy
  }, [items])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.listarPacotes()
        if (!mounted) return
        setItems(Array.isArray(data) ? data : [])
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao listar pacotes.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [refreshKey])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!detailsOpen) return
      if (!selected?.id) return
      setDetailsLoading(true)
      setDetailsError(null)
      try {
        const fresh = await apiService.buscarPacote(selected.id)
        if (!mounted) return
        setSelected(fresh)
      } catch (e: any) {
        if (!mounted) return
        setDetailsError(e?.message || 'Erro ao carregar detalhes do pacote.')
      } finally {
        if (!mounted) return
        setDetailsLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [detailsOpen, selected?.id])

  const resetCreateForm = () => {
    setVip(false)
    setWarzoneCoin(false)
    setNome('')
    setDescricao('')
    setPreco('29.90')
    setPontos('100')
    setDiasVip('30')
    setDescricaoVip('Acesso VIP')
    setImagem(null)
    setCreateError(null)
  }

  const openCreate = () => {
    resetCreateForm()
    setCreateOpen(true)
  }

  const closeCreate = () => {
    if (createLoading) return
    setCreateOpen(false)
  }

  const openDetails = (p: Pacote) => {
    setSelected(p)
    setDetailsError(null)
    setDetailsOpen(true)
  }

  const closeDetails = () => {
    if (detailsLoading) return
    setDetailsOpen(false)
  }

  const openEdit = async (p: Pacote) => {
    setDetailsOpen(false)
    setEditOpen(true)
    setEditError(null)
    setEditImagem(null)
    setEditLoading(true)
    try {
      const fresh = await apiService.buscarPacote(p.id)
      setEditData(fresh)
      setEditVip(!!fresh.vip)
      setEditWarzoneCoin(!!fresh.warzoneCoin)
      setEditNome(fresh.nome ?? '')
      setEditDescricao(fresh.descricao ?? '')
      setEditPreco(String(fresh.preco ?? 0))
      setEditPontos(String(fresh.pontos ?? 0))
      setEditDiasVip(String(fresh.diasVip ?? 0))
      setEditDescricaoVip(fresh.descricaoVip ?? '')
    } catch (e: any) {
      setEditError(e?.message || 'Erro ao carregar pacote para edição.')
    } finally {
      setEditLoading(false)
    }
  }

  const closeEdit = () => {
    if (editLoading) return
    setEditOpen(false)
  }

  const editPayload = (): PacoteInput => ({
    nome: editNome.trim(),
    descricao: editDescricao.trim(),
    preco: Number(editPreco),
    vip: editVip,
    warzoneCoin: editWarzoneCoin,
    pontos: Number(editPontos),
    diasVip: editVip ? Number(editDiasVip) : 0,
    descricaoVip: editVip ? (editDescricaoVip.trim() ? editDescricaoVip.trim() : null) : null
  })

  const validateEdit = (): string | null => {
    const p = editPayload()
    if (!p.nome) return 'Informe o nome.'
    if (!p.descricao) return 'Informe a descrição.'
    if (!Number.isFinite(p.preco) || p.preco <= 0) return 'Informe um preço válido.'
    if (!Number.isFinite(p.pontos) || p.pontos < 0) return 'Informe pontos válidos.'
    if (p.vip) {
      if (!Number.isFinite(p.diasVip) || p.diasVip <= 0) return 'Informe dias VIP válidos.'
    }
    return null
  }

  const onSaveEdit = async () => {
    if (!editData) return
    const err = validateEdit()
    if (err) {
      setEditError(err)
      return
    }

    setEditLoading(true)
    setEditError(null)
	    try {
	      const updated = await apiService.atualizarPacote(editData.id, editPayload())
	      setEditData(updated)
	      setSelected((cur) => (cur?.id === updated.id ? updated : cur))
	      setRefreshKey((v) => v + 1)
	      setEditOpen(false)
	    } catch (e: any) {
	      setEditError(e?.message || 'Erro ao salvar alterações.')
	    } finally {
	      setEditLoading(false)
	    }
	  }

  const onUpdateEditImage = async () => {
    if (!editData) return
    if (!editImagem) {
      setEditError('Selecione uma imagem.')
      return
    }

    setEditLoading(true)
    setEditError(null)
    try {
      const updated = await apiService.atualizarPacoteImagem(editData.id, editImagem)
      setEditData(updated)
      setSelected((cur) => (cur?.id === updated.id ? updated : cur))
      setEditImagem(null)
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setEditError(e?.message || 'Erro ao atualizar imagem.')
    } finally {
      setEditLoading(false)
    }
  }

  const requestDelete = (p: Pacote, context: 'list' | 'edit') => {
    setDeleteTarget(p)
    setDeleteContext(context)
    setDeleteError(null)
    setDeleteOpen(true)
  }

  const closeDelete = () => {
    if (deleteLoading) return
    setDeleteOpen(false)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      await apiService.apagarPacote(deleteTarget.id)

      if (selected?.id === deleteTarget.id) {
        setSelected(null)
        setDetailsOpen(false)
      }

      if (editData?.id === deleteTarget.id) {
        setEditData(null)
        setEditOpen(false)
      }

      if (deleteContext === 'edit') {
        setDetailsOpen(false)
        setSelected(null)
      }

      setDeleteOpen(false)
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setDeleteError(e?.message || 'Erro ao apagar pacote.')
    } finally {
      setDeleteLoading(false)
    }
  }

  const createPayload = (): PacoteInput => ({
    nome: nome.trim(),
    descricao: descricao.trim(),
    preco: Number(preco),
    vip,
    warzoneCoin,
    pontos: Number(pontos),
    diasVip: vip ? Number(diasVip) : 0,
    descricaoVip: vip ? (descricaoVip.trim() ? descricaoVip.trim() : null) : null
  })

  const validateCreate = (): string | null => {
    const p = createPayload()
    if (!p.nome) return 'Informe o nome.'
    if (!p.descricao) return 'Informe a descrição.'
    if (!Number.isFinite(p.preco) || p.preco <= 0) return 'Informe um preço válido.'
    if (!Number.isFinite(p.pontos) || p.pontos < 0) return 'Informe pontos válidos.'
    if (p.vip) {
      if (!Number.isFinite(p.diasVip) || p.diasVip <= 0) return 'Informe dias VIP válidos.'
    } else {
      if (!imagem) return 'Selecione uma imagem para o pacote.'
    }
    return null
  }

  const onCreate = async () => {
    const err = validateCreate()
    if (err) {
      setCreateError(err)
      return
    }

    setCreateLoading(true)
    setCreateError(null)
    try {
      const p = createPayload()
      const created = p.vip ? await apiService.criarPacoteVip(p) : await apiService.criarPacote(p, imagem as File)
      if (p.vip && imagem) {
        try {
          await apiService.atualizarPacoteImagem(created.id, imagem)
        } catch {
          // ignore: imagem opcional no fluxo VIP
        }
      }
      setCreateOpen(false)
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setCreateError(e?.message || 'Erro ao criar pacote.')
    } finally {
      setCreateLoading(false)
    }
  }

  const onDelete = async (p: Pacote) => {
    requestDelete(p, 'list')
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart />
            Gerenciar Pacotes
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            Cadastre, edite e exclua pacotes do Palworld.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => setRefreshKey((v) => v + 1)} disabled={loading}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} disabled={loading}>
            Adicionar pacote
          </Button>
        </Stack>
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pacote</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="center">Warzone Coin</TableCell>
                      <TableCell>Preço</TableCell>
                      <TableCell>Pontos</TableCell>
                      <TableCell>Dias VIP</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((p) => (
                      <TableRow
                        key={p.id}
                        hover
                        onClick={() => openDetails(p)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                            <Avatar
                              variant="rounded"
                              src={p.urlImagem ?? undefined}
                              alt={p.nome}
                              sx={{ width: 42, height: 42, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                            >
                              {String(p.nome ?? '?').slice(0, 1).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                {p.nome}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={p.vip ? 'VIP' : 'Pacote'} color={p.vip ? 'info' : 'default'} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={p.warzoneCoin ? 'Sim' : 'Não'}
                            color={p.warzoneCoin ? 'warning' : 'default'}
                            variant={p.warzoneCoin ? 'filled' : 'outlined'}
                          />
	                        </TableCell>
	                        <TableCell>{brl(Number(p.preco) || 0)}</TableCell>
	                        <TableCell>{p.pontos ?? 0}</TableCell>
	                        <TableCell>{p.vip ? (p.diasVip ?? 0) : '—'}</TableCell>
	                        <TableCell align="right">
	                          <Tooltip title="Editar">
	                            <IconButton
	                              size="small"
	                              onClick={(e) => {
	                                e.stopPropagation()
	                                openEdit(p)
	                              }}
	                            >
	                              <Edit fontSize="small" />
	                            </IconButton>
	                          </Tooltip>
	                          <Tooltip title="Apagar">
	                            <IconButton
	                              size="small"
	                              color="error"
	                              onClick={(e) => {
	                                e.stopPropagation()
	                                onDelete(p)
	                              }}
	                            >
	                              <Delete fontSize="small" />
	                            </IconButton>
	                          </Tooltip>
	                        </TableCell>
	                      </TableRow>
	                    ))}

                    {sorted.length === 0 && !error && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nenhum pacote encontrado.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={closeCreate}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: dialogBackdropSx }}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Adicionar pacote</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <FormControlLabel
              control={<Checkbox checked={vip} onChange={(e) => setVip(e.target.checked)} disabled={createLoading} />}
              label="VIP"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={warzoneCoin}
                  onChange={(e) => setWarzoneCoin(e.target.checked)}
                  disabled={createLoading}
                />
              }
              label="Warzone Coin"
            />

            <Divider />

            <TextField
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
              disabled={createLoading}
            />

            <TextField
              label="Descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
              required
              multiline
              minRows={3}
              disabled={createLoading}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Preço"
                value={preco}
                onChange={(e) => setPreco(e.target.value)}
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                fullWidth
                required
                disabled={createLoading}
              />
              <TextField
                label="Pontos"
                value={pontos}
                onChange={(e) => setPontos(e.target.value)}
                type="number"
                inputProps={{ step: '1', min: '0' }}
                fullWidth
                required
                disabled={createLoading}
              />
            </Stack>

            {vip && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Dias VIP"
                  value={diasVip}
                  onChange={(e) => setDiasVip(e.target.value)}
                  type="number"
                  inputProps={{ step: '1', min: '1' }}
                  fullWidth
                  required
                  disabled={createLoading}
                />
                <TextField
                  label="Descrição VIP"
                  value={descricaoVip}
                  onChange={(e) => setDescricaoVip(e.target.value)}
                  fullWidth
                  disabled={createLoading}
                />
              </Stack>
            )}

            <Divider />

            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 900 }}>Imagem</Typography>
              <Button variant="outlined" component="label" disabled={createLoading}>
                Selecionar imagem
                <input hidden type="file" accept="image/*" onChange={(e) => setImagem(e.target.files?.[0] ?? null)} />
              </Button>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {imagem ? imagem.name : vip ? 'Opcional para VIP (pode enviar depois).' : 'Obrigatório para pacote não-VIP.'}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreate} disabled={createLoading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={onCreate} disabled={createLoading}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={closeDetails}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: dialogBackdropSx }}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <span>Detalhes do pacote</span>
          <IconButton size="small" onClick={closeDetails} disabled={detailsLoading}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {detailsError && <Alert severity="error">{detailsError}</Alert>}
            {detailsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {selected && (
              <>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Avatar
                    variant="rounded"
                    src={selected.urlImagem ?? undefined}
                    alt={selected.nome}
                    sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {String(selected.nome ?? '?').slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                      {selected.nome}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                      <Chip size="small" label={selected.vip ? 'VIP' : 'Pacote'} color={selected.vip ? 'info' : 'default'} />
                      <Chip
                        size="small"
                        label={selected.warzoneCoin ? 'Warzone Coin' : 'Sem Warzone Coin'}
                        color={selected.warzoneCoin ? 'warning' : 'default'}
                        variant={selected.warzoneCoin ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Divider />

                <Typography sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {selected.descricao || '—'}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ flexWrap: 'wrap' }}>
                  <Chip size="small" label={`Preço: ${brl(Number(selected.preco) || 0)}`} />
                  <Chip size="small" label={`Pontos: ${selected.pontos ?? 0}`} />
                  {selected.vip ? <Chip size="small" label={`Dias VIP: ${selected.diasVip ?? 0}`} /> : <Chip size="small" label="Dias VIP: —" />}
                </Stack>

                {selected.vip && selected.descricaoVip && (
                  <Alert severity="info">
                    <strong>Descrição VIP:</strong> {selected.descricaoVip}
                  </Alert>
                )}

                {selected.urlImagem && (
                  <Box
                    component="img"
                    src={selected.urlImagem}
                    alt={`Imagem do pacote ${selected.nome}`}
                    sx={{
                      width: '100%',
                      maxHeight: 260,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.10)',
                      bgcolor: 'rgba(255,255,255,0.03)'
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => selected && openEdit(selected)}
            disabled={!selected || detailsLoading}
          >
            Editar
          </Button>
          <Button onClick={closeDetails} disabled={detailsLoading}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={closeEdit}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: dialogBackdropSx }}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <span>Editar pacote</span>
          <IconButton size="small" onClick={closeEdit} disabled={editLoading}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editError && <Alert severity="error">{editError}</Alert>}
            {editLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {editData && (
              <>
	                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
	                  <Avatar
	                    variant="rounded"
	                    src={editData.urlImagem ?? undefined}
	                    alt={editData.nome}
	                    sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
	                  >
	                    {String(editData.nome ?? '?').slice(0, 1).toUpperCase()}
	                  </Avatar>
	                </Stack>

                <FormControlLabel
                  control={<Checkbox checked={editVip} onChange={(e) => setEditVip(e.target.checked)} disabled={editLoading} />}
                  label="VIP"
                />
                <FormControlLabel
                  control={<Checkbox checked={editWarzoneCoin} onChange={(e) => setEditWarzoneCoin(e.target.checked)} disabled={editLoading} />}
                  label="Warzone Coin"
                />

                <Divider />

                <TextField
                  label="Nome"
                  value={editNome}
                  onChange={(e) => setEditNome(e.target.value)}
                  fullWidth
                  required
                  disabled={editLoading}
                />

                <TextField
                  label="Descrição"
                  value={editDescricao}
                  onChange={(e) => setEditDescricao(e.target.value)}
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  disabled={editLoading}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Preço"
                    value={editPreco}
                    onChange={(e) => setEditPreco(e.target.value)}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    fullWidth
                    required
                    disabled={editLoading}
                  />
                  <TextField
                    label="Pontos"
                    value={editPontos}
                    onChange={(e) => setEditPontos(e.target.value)}
                    type="number"
                    inputProps={{ step: '1', min: '0' }}
                    fullWidth
                    required
                    disabled={editLoading}
                  />
                </Stack>

                {editVip && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Dias VIP"
                      value={editDiasVip}
                      onChange={(e) => setEditDiasVip(e.target.value)}
                      type="number"
                      inputProps={{ step: '1', min: '1' }}
                      fullWidth
                      required
                      disabled={editLoading}
                    />
                    <TextField
                      label="Descrição VIP"
                      value={editDescricaoVip}
                      onChange={(e) => setEditDescricaoVip(e.target.value)}
                      fullWidth
                      disabled={editLoading}
                    />
                  </Stack>
                )}

                <Divider />

                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>Atualizar imagem</Typography>
                  <Button variant="outlined" component="label" disabled={editLoading}>
                    Selecionar imagem
                    <input hidden type="file" accept="image/*" onChange={(e) => setEditImagem(e.target.files?.[0] ?? null)} />
                  </Button>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {editImagem ? editImagem.name : 'Nenhum arquivo selecionado.'}
                  </Typography>
                  <Button variant="contained" onClick={onUpdateEditImage} disabled={editLoading || !editImagem}>
                    Enviar imagem
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => editData && requestDelete(editData, 'edit')}
            disabled={!editData || editLoading}
          >
            Apagar
          </Button>
          <Button onClick={closeEdit} disabled={editLoading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={onSaveEdit} disabled={!editData || editLoading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        fullWidth
        maxWidth="xs"
        BackdropProps={{ sx: dialogBackdropSx }}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Typography sx={{ color: 'text.secondary' }}>
              Tem certeza que deseja apagar o pacote <strong>{deleteTarget?.nome ?? '—'}</strong>? Essa ação não pode ser desfeita.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDelete} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" startIcon={<Delete />} onClick={confirmDelete} disabled={deleteLoading || !deleteTarget}>
            {deleteLoading ? 'Apagando...' : 'Apagar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminPacotesPage
